import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, IconButton, MenuItem, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, Pagination,
} from '@mui/material';
import { Add as AddIcon, Visibility } from '@mui/icons-material';
import { SupplierBillService } from '../../services/supplierBill.service';
import { SupplierService } from '../../services/supplier.service';
import { SupplierBill, SUPPLIER_BILL_STATUS_COLORS } from '../../interfaces/procurement/supplierBill.interface';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { useToast } from '../../context/ToastContext';

const STATUS_COLORS = SUPPLIER_BILL_STATUS_COLORS;

export const SupplierBillList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [bills, setBills] = useState<SupplierBill[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);
  const [loadingBills, setLoadingBills] = useState(false);
  const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });

  useEffect(() => {
    SupplierService.listByInstitution(0, 500)
      .then(r => setSuppliers(r.content))
      .catch(e => toast.showApiError(e, 'Failed to load suppliers'))
      .finally(() => setLoadingSuppliers(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBills = async (supplierId: string, page = 0) => {
    if (!supplierId) return;
    setLoadingBills(true);
    try {
      const response = await SupplierBillService.listBySupplier(supplierId, page, 10);
      setBills(response.content);
      setPageInfo({ currentPage: response.number, totalPages: response.totalPages, totalElements: response.totalElements });
    } catch (e) {
      toast.showApiError(e, 'Failed to load supplier bills');
    } finally {
      setLoadingBills(false);
    }
  };

  const handleSupplierChange = (supplierId: string) => {
    setSelectedSupplierId(supplierId);
    setBills([]);
    setPageInfo({ currentPage: 0, totalPages: 0, totalElements: 0 });
    fetchBills(supplierId, 0);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    fetchBills(selectedSupplierId, page - 1);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Supplier Bills</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/procurement/supplier-bills/new')}>
          New Supplier Bill
        </Button>
      </Box>

      <Box sx={{ mb: 3, maxWidth: 400 }}>
        <TextField
          select
          label="Supplier"
          value={selectedSupplierId}
          onChange={e => handleSupplierChange(e.target.value)}
          fullWidth
          disabled={loadingSuppliers}
        >
          {suppliers.map(s => (
            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
          ))}
        </TextField>
      </Box>

      {!selectedSupplierId ? (
        <Typography color="text.secondary">Pick a supplier to view their bills.</Typography>
      ) : loadingBills ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>Total: {pageInfo.totalElements}</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Bill Number</TableCell>
                  <TableCell>Supplier Invoice Ref</TableCell>
                  <TableCell>PO Number</TableCell>
                  <TableCell>Bill Date</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bills.map(bill => (
                  <TableRow key={bill.id}>
                    <TableCell>{bill.billNumber}</TableCell>
                    <TableCell>{bill.supplierInvoiceRef ?? '-'}</TableCell>
                    <TableCell>{bill.purchaseOrderId}</TableCell>
                    <TableCell>{bill.billDate}</TableCell>
                    <TableCell>{bill.dueDate}</TableCell>
                    <TableCell>
                      <Chip label={bill.status} color={STATUS_COLORS[bill.status]} size="small" />
                    </TableCell>
                    <TableCell>{bill.totalAmount}</TableCell>
                    <TableCell>{bill.paidAmount}</TableCell>
                    <TableCell>
                      <Tooltip title="View" arrow>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/procurement/supplier-bills/${bill.id}`)}
                          aria-label="View"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {pageInfo.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pageInfo.totalPages}
                page={pageInfo.currentPage + 1}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SupplierBillList;
